import { Component, Input, OnDestroy } from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SelectInPopoverOption } from 'src/app/shared/components/select-in-popover/select-in-popover-option';
import { BasicService } from 'src/app/shared/services/basic.service';
import { EditorConfig } from '../../../editor-config';
import { CodeMirrorWrapperComponent } from '../code-mirror-wrapper/codemirror-wrapper.component';
import { ClickedMarksAndMouseEvent } from '../code-mirror-wrapper/marks-helper';
import { CodemirrorContext } from '../codemirror-context';
import {
  CodemirrorSpellChecker,
  LAST_SET_SPELLCHECKER_LANG_LS_KEY,
  SpellCheckerContext,
} from './codemirror-spellchecker';
import { HunspellService } from './hunspell.service';
import { SpellcheckerLanguageSelectionComponent } from './spellchecker-language-selection.component';

/**
 * Displays a flag that indicates the current spellchecker language,
 * and when clicked, offers a drop-down menu to select a different language
 */
@Component({
  selector: 'app-codemirror-spellchecker',
  templateUrl: './codemirror-spellchecker.component.html',
  styleUrls: ['./codemirror-spellchecker.component.scss'],
})
export class CodemirrorSpellcheckerComponent implements OnDestroy {
  @Input() editorConfig: EditorConfig;
  @Input() codemirrorContext: CodemirrorContext;

  spellChecker: CodemirrorSpellChecker;

  private ngOnDestroy$ = new Subject();

  constructor(private basicService: BasicService, private hunspellService: HunspellService) {}

  // The CodeMirrorWrapperComponent is passsed by the parent component
  // via this method instead of as an Input()
  // to avoid ExpressionChangedAfterItHasBeenCheckedError in single-message-editor
  // (and we cannot delay the init of the component otherwise the toolbar would glitch when adding a new tweet)
  init(codeMirrorWrapper: CodeMirrorWrapperComponent) {
    // And we need a timeout here, to avoid a ExpressionChangedAfterItHasBeenCheckedError in this component
    setTimeout(() => this.doInit(codeMirrorWrapper));
  }

  private doInit(codeMirrorWrapper: CodeMirrorWrapperComponent) {
    const editor = codeMirrorWrapper.ctx.codeMirror;
    const context = new TPPSpellCheckerContext();
    this.spellChecker = new CodemirrorSpellChecker(editor, this.hunspellService, context, this.codemirrorContext);
    this.spellChecker.setLanguageCode(this.editorConfig.spellCheckerLanguageCode);
    codeMirrorWrapper.marksClicked$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(marksAndEvent => {
      this.onMarksClicked(marksAndEvent);
    });
  }

  ngOnDestroy() {
    this.spellChecker?.cleanUp();
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  openLanguageMenu(event) {
    this.basicService.popoverWithResult(event, SpellcheckerLanguageSelectionComponent, {
      spellChecker: this.spellChecker,
    });
  }

  private async onMarksClicked(marksAndEvent: ClickedMarksAndMouseEvent) {
    const suggestions = this.spellChecker.suggestionsForClickedMarks(marksAndEvent.marks);

    // Suggestions will be null if the clicked marked didn't belong to the spellchecker.
    // It will also be empty if the spellchecker didn't find any suggestion (although this is rare),
    // in which case I assume it is better to open an empty menu (not opening would look like a bug)
    if (suggestions) {
      const options = suggestions.map(suggestion => new SelectInPopoverOption(suggestion));
      const modalResult = await this.basicService.selectInPopover(marksAndEvent.event, options);
      if (modalResult.data) {
        this.spellChecker.replacementClicked(modalResult.data.label);
      }
    }
  }
}

class TPPSpellCheckerContext implements SpellCheckerContext {
  getLastSetSpellcheckerLang(): string {
    return localStorage.getItem(LAST_SET_SPELLCHECKER_LANG_LS_KEY);
  }
  saveLastSetSpellcheckerLang(lang: string): void {
    localStorage.setItem(LAST_SET_SPELLCHECKER_LANG_LS_KEY, lang);
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSpellSuggestionsFromOutsideOfAngular(suggestions: any) {
    // only  useful for angularjs editor2
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSpellSuggestions(suggestions: any): void {
    // only  useful for angularjs editor2
  }
}
